import type { TileStackView } from '../board/TileStackView';
import type { BoardView, StackViewProps } from '../board/BoardView';
import { soundManager } from '../../../../app/SoundManager';
import MathUtils from '../../../../utils/MathUtils';
import { StarBlockerView } from '../blockers/StarBlockerView';

export class BoosterMoveView {
  private targetView: TileStackView | null = null;

  constructor(
    private readonly boardView: BoardView,
    private readonly originView: TileStackView,
  ) {
    soundManager.play(`Gameplay_Stack_Pickup`);
    originView.zIndex += 1000000;
  }

  public setSelectedTarget(targetView: TileStackView | null): void {
    if (this.targetView === targetView) return;

    this.deselectCurrent();

    this.targetView = targetView;

    if (!this.targetView) return;

    this.targetView.props(this.boardView.getStackViewProps(this.originView.model));
    this.targetView.animateBounce();
    this.targetView.fadeIn();

    this.setUnderTileBlockerVisibility(false);
  }

  private deselectCurrent(): void {
    if (!this.targetView) return;
    this.targetView.props(this.boardView.getStackViewProps(this.targetView.model));
    this.targetView.animateBounce();

    this.setUnderTileBlockerVisibility(true);
  }

  private async animateStackToPosition(view: TileStackView, props: StackViewProps): Promise<void> {
    const distance = MathUtils.getDistanceBetween(view, props);
    const duration = MathUtils.clamp(distance * 0.5, 150, 500);

    await view
      .tweenTo(
        {
          x: props.x,
          y: props.y,
        },
        duration,
        'quadOut',
        true,
      )
      .asyncOnComplete();

    soundManager.play(`Gameplay_Stack_Placement`);
    view.zIndex = props.zIndex;
  }

  public async reset(): Promise<void> {
    this.deselectCurrent();
    await this.animateStackToPosition(this.originView, this.boardView.getStackViewProps(this.originView.model));
  }

  public async animateOriginToTarget(): Promise<void> {
    if (!this.targetView) return;
    await this.animateStackToPosition(this.originView, this.boardView.getStackViewProps(this.targetView.model));
  }

  private setUnderTileBlockerVisibility(visible: boolean): void {
    const blocker = this.targetView?.model.underTileBlocker;
    if (!blocker) return;

    const underTileBlocker = this.boardView.getBlockerView(blocker.id);

    if (!underTileBlocker || !(underTileBlocker instanceof StarBlockerView)) return;

    if (visible) {
      underTileBlocker.showSprite();
    } else {
      underTileBlocker.hideSprite();
    }
  }
}
